import React, { useState, useEffect } from 'react';
import {
  useSearchParams,
  useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { avalesAPI } from '../services/api';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Card,
  Icon,
  Alert,
  Textarea,
  Spinner,
  Field,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Divider } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import { FaHandshake, FaMusic, FaMapMarkerAlt, FaCheck, FaSignInAlt } from 'react-icons/fa';

const OtorgarAval = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bandaInfo, setBandaInfo] = useState(null);
  const [texto, setTexto] = useState('');
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    const verificarToken = async () => {
      if (!token) {
        setError('No se proporcionó un token válido');
        setLoading(false);
        return;
      }

      try {
        const response = await avalesAPI.verificarToken(token);
        setBandaInfo(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error verificando token:', err);
        setError(err.response?.data?.message || 'Token inválido o expirado');
        setLoading(false);
      }
    };

    verificarToken();
  }, [token]);

  const handleOtorgarAval = async () => {
    if (!user) {
      // Save the current URL and redirect to login
      localStorage.setItem('redirectAfterLogin', window.location.href);
      navigate('/');
      toaster.create({
        title: 'Inicia sesión',
        description: 'Necesitas iniciar sesión como cofradía para otorgar el aval',
        type: 'info',
        duration: 5000,
      });
      return;
    }

    if (user?.userType !== 'COFRADIA') {
      toaster.create({
        title: 'Acción no permitida',
        description: 'Solo las cofradías pueden otorgar avales',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setSubmitting(true);
    try {
      await avalesAPI.completarConToken(token, texto);
      setCompleted(true);
      toaster.create({
        title: 'Aval otorgado',
        description: `Has otorgado un aval de confianza a ${bandaInfo?.banda?.nombre}`,
        type: 'success',
        duration: 5000,
      });
    } catch (err) {
      console.error('Error otorgando aval:', err);
      toaster.create({
        title: 'Error',
        description: err.response?.data?.message || 'No se pudo otorgar el aval',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <VStack gap={4}>
          <Spinner size="xl" color="green.500" borderWidth="4px" />
          <Text color="gray.600">Verificando solicitud...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="lg">
          <Card.Root>
            <Card.Body>
              <VStack gap={6} py={8}>
                <Icon w={16} h={16} color="gray.300" asChild><FaHandshake /></Icon>
                <Heading size="lg" textAlign="center" color="gray.700">
                  Solicitud no válida
                </Heading>
                <Alert.Root status="error" rounded="lg">
                  <Alert.Indicator />
                  {error}
                </Alert.Root>
                <Button colorPalette="purple" onClick={() => navigate('/')}>
                  Ir al inicio
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </Container>
      </Box>
    );
  }

  if (completed) {
    return (
      <Box minH="100vh" bg="gray.50" py={20}>
        <Container maxW="lg">
          <Card.Root>
            <Card.Body>
              <VStack gap={6} py={8}>
                <Box p={4} bg="green.100" rounded="full">
                  <Icon w={12} h={12} color="green.500" asChild><FaCheck /></Icon>
                </Box>
                <Heading size="lg" textAlign="center" color="gray.700">
                  ¡Aval otorgado correctamente!
                </Heading>
                <Text color="gray.600" textAlign="center">
                  Has otorgado un aval de confianza a <strong>{bandaInfo?.banda?.nombre}</strong>.
                  La banda recibirá una notificación y podrá aceptar tu aval.
                </Text>
                <Button colorPalette="purple" onClick={() => navigate('/cofradia/perfil')}>
                  Ir a mi perfil
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={10}>
      <Container maxW="lg">
        <VStack gap={6}>
          {/* Header */}
          <VStack gap={2} textAlign="center">
            <Icon w={12} h={12} color="green.500" asChild><FaHandshake /></Icon>
            <Heading size="xl" color="gray.800">
              Solicitud de Aval
            </Heading>
            <Text color="gray.600">
              Has recibido una solicitud para avalar una banda
            </Text>
          </VStack>

          {/* Banda Info Card */}
          <Card.Root w="full" shadow="lg">
            <Card.Body>
              <VStack gap={4}>
                <Avatar.Root size="2xl" bg="purple.100" color="purple.600"><Avatar.Fallback name={bandaInfo?.banda?.nombre}>{<Icon w={10} h={10} asChild><FaMusic /></Icon>}</Avatar.Fallback><Avatar.Image src={bandaInfo?.banda?.avatar} /></Avatar.Root>
                <VStack gap={1}>
                  <Heading size="lg" color="gray.800">
                    {bandaInfo?.banda?.nombre}
                  </Heading>
                  {bandaInfo?.banda?.localizacion && (
                    <HStack color="gray.500">
                      <Icon asChild><FaMapMarkerAlt /></Icon>
                      <Text>
                        {bandaInfo.banda.localizacion.ciudad}, {bandaInfo.banda.localizacion.provincia}
                      </Text>
                    </HStack>
                  )}
                </VStack>
                {bandaInfo?.banda?.bio && (
                  <Text color="gray.600" textAlign="center" fontSize="sm">
                    {bandaInfo.banda.bio}
                  </Text>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Explanation */}
          <Alert.Root status="info" rounded="lg">
            <Alert.Indicator />
            <Box>
              <Text fontWeight="bold">¿Qué es un Aval de Confianza?</Text>
              <Text fontSize="sm">
                Al otorgar un aval, certificas que conoces esta banda y puedes dar fe de su
                profesionalidad. Tu aval aparecerá públicamente en el perfil de la banda.
              </Text>
            </Box>
          </Alert.Root>

          {/* Action Card */}
          <Card.Root w="full" shadow="lg">
            <Card.Body>
              <VStack gap={4}>
                {!user ? (
                  <>
                    <Text color="gray.600" textAlign="center">
                      Para otorgar el aval, necesitas iniciar sesión con tu cuenta de cofradía
                    </Text>
                    <Button
                      colorPalette="purple"
                      size="lg"
                      onClick={() => {
                        localStorage.setItem('redirectAfterLogin', window.location.href);
                        navigate('/');
                      }}
                      w="full"><Icon asChild><FaSignInAlt /></Icon>Iniciar sesión
                                          </Button>
                    <Text fontSize="sm" color="gray.500" textAlign="center">
                      ¿No tienes cuenta? Podrás registrarte como cofradía
                    </Text>
                  </>
                ) : user?.userType !== 'COFRADIA' ? (
                  <Alert.Root status="warning" rounded="lg">
                    <Alert.Indicator />
                    <Box>
                      <Text fontWeight="bold">Cuenta incorrecta</Text>
                      <Text fontSize="sm">
                        Has iniciado sesión como {user?.userType?.toLowerCase()}, pero solo las
                        cofradías pueden otorgar avales. Por favor, inicia sesión con una cuenta
                        de cofradía.
                      </Text>
                    </Box>
                  </Alert.Root>
                ) : (
                  <>
                    <Field.Root>
                      <Field.Label>Mensaje opcional</Field.Label>
                      <Textarea
                        placeholder="Ej: Hemos trabajado con esta banda durante 5 años y siempre han sido muy profesionales..."
                        value={texto}
                        onChange={(e) => setTexto(e.target.value)}
                        rows={4}
                      />
                    </Field.Root>
                    <Button
                      colorPalette="green"
                      size="lg"
                      onClick={handleOtorgarAval}
                      loading={submitting}
                      loadingText="Otorgando aval..."
                      w="full"><Icon asChild><FaHandshake /></Icon>Otorgar Aval de Confianza
                                          </Button>
                  </>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Footer info */}
          <Text fontSize="sm" color="gray.500" textAlign="center">
            Cadencya - Plataforma de conexión para la Semana Santa
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default OtorgarAval;
